import { Controller } from "@hotwired/stimulus"
import { getCSRFToken } from "web-ui/csrf"
import * as Log from "web-ui/log"

// Connects to data-controller="option-list"
export default class extends Controller {
  static targets = [
    "search",
    "option",
    "checkbox",
    "group",
    "noSearchResults",
    "clearButton",
    "scrollIndicator"
  ]
  static values = { url: String }

  connect() {
    this.debug("Connect")

    this.activeIndex = null
    this.setActive(0)

    this.focusSearch()
  }

  disconnect() {
    this.debug("Disconnect")
  }

  searchTargetConnected(element) {
    element.addEventListener("input", this.onSearchInput)
    element.addEventListener("keydown", this.onSearchKeyDown)
  }

  searchTargetDisconnected(element) {
    element.removeEventListener("input", this.onSearchInput)
    element.removeEventListener("keydown", this.onSearchKeyDown)
  }

  optionTargetConnected(element) {
    element.addEventListener("mousedown", this.onOptionMouseDown)
    element.addEventListener("click", this.onOptionClick)
    element.addEventListener("mousemove", this.onOptionMouseMove)
  }

  optionTargetDisconnected(element) {
    element.removeEventListener("mousedown", this.onOptionMouseDown)
    element.removeEventListener("click", this.onOptionClick)
    element.removeEventListener("mousemove", this.onOptionMouseMove)
  }

  checkboxTargetConnected(element) {
    element.addEventListener("change", this.onCheckboxChange)
  }

  checkboxTargetDisconnected(element) {
    element.removeEventListener("change", this.onCheckboxChange)
  }

  clearButtonTargetConnected(element) {
    element.addEventListener("click", this.clearSearch)
  }

  clearButtonTargetDisconnected(element) {
    element.removeEventListener("click", this.clearSearch)
  }

  onOptionMouseDown = (event) => {
    // Cancel focus shift so the search input remains focused - Matt, Taylor, Tue May 19 2026
    event.preventDefault()
  }

  onOptionClick = (event) => {
    const option = event.currentTarget
    const checkbox = option.querySelector('input[type="checkbox"]')

    // Let the default handler toggle and dispatch change - Matt, Taylor, Tue May 19 2026
    if (event.target === checkbox) {
      return
    }

    // Cancel label activation (toggles checkbox and focuses it); change event is dispatched manually - Matt, Taylor, Tue May 19 2026
    event.preventDefault()

    checkbox.checked = !checkbox.checked
    checkbox.dispatchEvent(new Event("change", { bubbles: true }))
  }

  onOptionMouseMove = (event) => {
    const option = event.currentTarget
    const index = this.visibleOptions().indexOf(option)

    if (index === -1) {
      return
    }

    this.setActive(index, false)
  }

  onCheckboxChange = (event) => {
    const checkbox = event.currentTarget
    const method = checkbox.checked ? "POST" : "DELETE"

    this.submit(checkbox.value, method)
  }

  onSearchInput = (event) => {
    this.applyFilter(event.currentTarget.value)
    this.setActive(0)
  }

  onSearchKeyDown = (event) => {
    if (event.key === "ArrowDown") {
      event.preventDefault()
      this.moveActive(1)
    } else if (event.key === "ArrowUp") {
      event.preventDefault()
      this.moveActive(-1)
    } else if (event.key === "Enter") {
      event.preventDefault()
      this.toggleActiveCheckbox()
    }
  }

  focusSearch() {
    setTimeout(() => {
      this.searchTarget.focus()
    }, 0)
  }

  clearSearch = () => {
    if (!this.hasSearchTarget) {
      return
    }

    this.searchTarget.value = ""
    this.applyFilter("")
    this.setActive(0)
    this.focusSearch()
  }

  applyFilter(term) {
    const searchSegments = term.toLowerCase().split(/\s+/).filter((segment) => segment !== "")

    this.optionTargets.forEach((option) => {
      const optionText = option.textContent.toLowerCase()

      if (searchSegments.every((segment) => optionText.includes(segment))) {
        option.hidden = false
      } else {
        option.hidden = true
      }
    })

    this.groupTargets.forEach((group) => {
      const visibleOptions = group.querySelectorAll('[data-option-list-target="option"]:not([hidden])')
      group.hidden = visibleOptions.length === 0
    })

    this.noSearchResultsTarget.hidden = this.visibleOptions().length !== 0

    this.scrollIndicatorTarget.displayIndicators()
  }

  moveActive(delta) {
    const visible = this.visibleOptions()

    if (visible.length === 0) {
      return
    }

    const nextIndex = Math.max(0, Math.min(visible.length - 1, this.activeIndex + delta))

    this.setActive(nextIndex)
  }

  setActive(index, scroll = true) {
    this.optionTargets.forEach((option) => option.classList.remove("active"))

    const visible = this.visibleOptions()

    if (visible.length === 0) {
      this.activeIndex = null
      return
    }

    this.activeIndex = index

    const active = visible[index]

    if (active) {
      active.classList.add("active")

      if (scroll) {
        active.scrollIntoView({ block: "nearest" })
      }
    }
  }

  visibleOptions() {
    return this.optionTargets.filter((option) => !option.hidden)
  }

  toggleActiveCheckbox() {
    if (this.activeIndex == null) {
      return
    }

    const visible = this.visibleOptions()
    const option = visible[this.activeIndex]

    if (!option) {
      return
    }

    const checkbox = option.querySelector('input[type="checkbox"]')

    checkbox.checked = !checkbox.checked
    checkbox.dispatchEvent(new Event("change", { bubbles: true }))
  }

  async submit(value, method) {
    this.debug("Submit", { value, method })

    const params = new URLSearchParams()
    params.set("value", value)

    this.element.setAttribute("aria-busy", "true")

    try {
      const response = await fetch(this.urlValue, {
        method,
        headers: {
          "Accept": "text/vnd.turbo-stream.html",
          "X-CSRF-Token": getCSRFToken(),
          "Content-Type": "application/x-www-form-urlencoded"
        },
        body: params
      })

      const html = await response.text()
      Turbo.renderStreamMessage(html)
    } catch (error) {
      Sentry.captureException(error)
    } finally {
      this.element.removeAttribute("aria-busy")
    }
  }

  debug(message, data) {
    Log.debug("Option List - " + message, data)
  }
}
